import { Component, HostBinding, OnDestroy, ViewChild } from '@angular/core';
import { ContextMenuOption } from '../../../../models/context-menu-option';
import { Endpoint, HelpIndex, SettingsType, OperationType, MessageType } from '../../../../models/enums';
import { AlignDirection, CellTemplateType, GenericColDef, GridContext, SortByFilter, GridRowSelectionType, CellActionType } from '../../../../models/grid-context';
import * as _ from 'underscore';
import { SettingsNavigationContentComponent } from '../../../../models/settings-navigation-content-component';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { DeleteEntityModalComponent } from '../../delete-entity-modal/delete-entity-modal.component';
import { GenericGridComponent } from '../../../generic-grid/generic-grid.component';
import { ActionType, ObjectActionListType, PermissionSet, Permissions } from '../../../../models/permission';
import { StorageModalComponent, StorageUseType } from '../../storage-modal/storage-modal.component';
import { Storage } from '../../../../models/storage';
import { CellClickedEvent, RowDragEvent, RowNode } from 'ag-grid-community';
import { Subscription } from 'rxjs/internal/Subscription';

@Component({
    standalone: false,
    selector: 'res-system-storage',
    templateUrl: './system-storage.component.html',
    styleUrls: ['./system-storage.component.scss']
})
export class SystemsStorageComponent implements SettingsNavigationContentComponent, OnDestroy {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild('storagesGrid', { static: true }) storagesGrid: GenericGridComponent;
    @ViewChild('storageDetailsGrid', { static: true }) storageDetailsGrid: GenericGridComponent;

    readonly readOnlyModeString = 'Read-only';

    private updateStoragesList: Subscription;

    automationId = 'storages-list';
    detailsAutomationId = 'organizations-list'
    isDirty: boolean;
    hasCheckboxChanges = false;
    hasDragDropChanges = false;
    validChanges = true;
    gridName = 'Storages';

    isBusy = false;
    isBusyDetails = false;
    isBusyMessage = 'Loading Storages ...';
    isBusyMessageDetails = 'Loading ...'
    helpIndex = HelpIndex.sys_storage_summary_dlg;
    allStorages;
    initialStorageHandles = [];
    indexStorageExists = false;

    // permissions
    hasSystemStorageAddEditPermissions: boolean;
    hasContextMenuPermissions = true;

    allUsers = [];
    roleList = [];
    organizations: Array<any> = [];
    storagesGridContext = new GridContext({
        sortTypeEnabled: false,
        sortEnabled: false,
        inlineGridFilter: true
    });

    storageDetailsGridContext = new GridContext({
        sortTypeEnabled: false,
        sortEnabled: false,
        sortAscending: true,
        sortBy: SortByFilter.NAME,
    });

    pinnedRowData = [];
    storagesColumnDefs = new Array<GenericColDef>(
        {
            headerName: 'Type',
            field: 'storageUseTypeDisplay',
            automationId: this.automationId,
            maxWidth: 110,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip,
            lockVisible: true,
            rowDrag: (params) => { return params.data.storageUseType !== StorageUseType.INDEX_STORE && this.hasSystemStorageAddEditPermissions; },
        },
        {
            headerName: 'Name',
            field: 'name',
            automationId: this.automationId,
            minWidth: 30,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip,
            suppressMovable: true,
            lockVisible: true,
        },
        {
            headerName: 'Enabled',
            colId: 'inUseId',
            field: 'inUse',
            maxWidth: 70,
            width: 70,
            automationId: this.automationId,
            cellClass: 'green-checkbox',
            cellTemplateType: CellTemplateType.checkbox,
            cellStyle: () => {
                if (!this.hasSystemStorageAddEditPermissions) {
                    return { 'pointer-events': 'none', opacity: '0.4' }
                }
                return null;
            }
        },
        {
            headerName: 'Mode',
            field: 'readOnlyDisplay',
            maxWidth: 90,
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip,
            cellAlignDirection: AlignDirection.center,
            cellStyle: params => {
                if (params?.value === this.readOnlyModeString) {
                    return { color: '#996600' };
                }
                return null;
            }
        },
        {
            headerName: 'Deactivate above',
            field: 'deactivationThreshold',
            maxWidth: 122,
            width: 122,
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.editableDeactivatedThesholdInput,
            cellAlignDirection: AlignDirection.right,
        },
        {
            headerName: 'Reactivate below',
            field: 'reactivationThreshold',
            maxWidth: 122,
            width: 122,
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.editableReactivatedThesholdInput,
            cellAlignDirection: AlignDirection.right,
        },
        {
            headerName: 'Used',
            field: 'currentUsage',
            automationId: this.automationId,
            maxWidth: 55,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip,
            suppressMovable: true,
            headerAlignDirection: AlignDirection.right,
            cellAlignDirection: AlignDirection.right,
            cellStyle: params => {
                if (params?.value && !params?.data?.inService && params?.data?.inUse && params?.data?.readOnlyDisplay === this.readOnlyModeString) {
                    const usedValue = params.value.split(' ');

                    if (parseInt(usedValue[0]) > params.data.deactivationThreshold) {
                        return { color: '#CC0000' };
                    }
                    if (parseInt(usedValue[0]) > params.data.reactivationThreshold) {
                        return { color: '#996600' };
                    }
                }
                return null;
            }
        },
        {
            headerName: 'Available',
            field: 'storageAvailable',
            automationId: this.automationId,
            maxWidth: 90,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip,
            headerAlignDirection: AlignDirection.right,
            cellAlignDirection: AlignDirection.right,
        },
        {
            headerName: 'Total',
            field: 'storageTotal',
            automationId: this.automationId,
            maxWidth: 90,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip,
            suppressMovable: true,
            headerAlignDirection: AlignDirection.right,
            cellAlignDirection: AlignDirection.right,
        },
        {
            headerName: 'Path',
            field: 'networkPath',
            minWidth: 30,
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip,
        },
        {
            headerName: 'File System',
            field: 'facilityType',
            automationId: this.automationId,
            maxWidth: 90,
            width: 90,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip,
        },
        {
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.contextMenu,
            minWidth: 30, maxWidth: 30, width: 30,
            suppressSizeToFit: true
        }
    );

    storageDetailsColumnDefs = new Array<GenericColDef>(
        {
            headerName: 'Organization Name',
            field: 'name',
            automationId: this.detailsAutomationId,
            minWidth: 60,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip,
            suppressMovable: true,
            lockVisible: true,
            defaultSortBy: SortByFilter.NAME,
            defaultSortAscending: true,
        },
        {
            headerName: 'Date Created',
            field: 'createdOn',
            automationId: this.detailsAutomationId,
            minWidth: 60, width: 145,
            headerAlignDirection: AlignDirection.right,
            cellAlignDirection: AlignDirection.right,
            cellTemplateType: CellTemplateType.date,
            suppressMovable: true,
        },
        {
            headerName: 'Storage Used',
            field: 'storageUsed',
            automationId: this.detailsAutomationId,
            minWidth: 60, width: 100,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip,
            headerAlignDirection: AlignDirection.right,
            cellAlignDirection: AlignDirection.right,
        },
        {
            headerName: 'Date Last Calculated',
            field: 'lastCalculated',
            automationId: this.detailsAutomationId,
            minWidth: 60, width: 145,
            headerAlignDirection: AlignDirection.right,
            cellAlignDirection: AlignDirection.right,
            cellTemplateType: CellTemplateType.date,
            suppressMovable: true,
        },
    );

    contextMenuOptions = [
        new ContextMenuOption({
            name: 'View / Edit ...',
            iconClass: 'fa-pencil',
            iconColor: 'color-blue-01',
            automationId: 'storages-view-edit',
            click: (item) => this.openViewEditModal(item),
        }),
        new ContextMenuOption({
            name: 'Delete', iconClass: 'fa-trash', iconColor: 'color-red-05', automationId: 'storages-delete',
            click: (item) => this.openDeleteModal(item),
            enabled: (item) => item?.noOfAttachedOrgs === 0,
            permissions: [new PermissionSet({ permissionType: ObjectActionListType.STORAGE, permissionActions: [ActionType.DELETE] })]
        })
    ];
    storagesSelection = GridRowSelectionType.single;
    selectedStorage;

    rowClassRules = {
        'storage-document-row': (params) => {
            return (params.data.storageUseType === StorageUseType.DOCUMENT_STORE);
        },
        'storage-index-row': (params) => {
            return (params.data.storageUseType === StorageUseType.INDEX_STORE);
        }
    }

    constructor(private commonService: CommonService, private restService: RestService) {
        this.updateStoragesList = this.commonService.getMessage(MessageType.updateStoragesList).subscribe(() => {
            if (this.isDisabled() && !this.isBusy) {
                this.loadGrid(true);
            }
        });
    }

    onSelectionChanged = (event) => {
        if (event?.api && event.source !== 'apiSelectAll') {
            const selectedNodeData = event.api.getSelectedRows()[0];
            this.selectedStorage = selectedNodeData ?? event.data;
            if (this.selectedStorage) {
                this.isBusyDetails = true;
                this.restService.post(Endpoint.realmManager_listCustomersForStorageFacility, { handle: this.selectedStorage.handle }).then(customersResponse => {
                    if (customersResponse?.organizations?.length > 0) {
                        customersResponse.organizations.forEach(value => {
                            value.storageUsed = value.totalStorageUsage > 0 ? this.commonService.formatSizeUnits(value.totalStorageUsage * 1024) : '';
                            value.lastCalculated = value.totalStorageUsage > 0 ? value.storageUsages[0].lastCalculated : 'Not Calculated';
                            if (value?.createdOn?.length > 0) {
                                value.createdOn = this.commonService.insertYesterday(value.createdOn);
                            }
                            if (value?.lastCalculated?.length > 0) {
                                value.lastCalculated = this.commonService.insertYesterday(value.lastCalculated);
                            }
                        })
                        this.selectedStorage.organizations = customersResponse.organizations;
                        this.selectedStorage.noOfAttachedOrgs = customersResponse.organizations.length;
                    }
                    this.isBusyDetails = false;
                    this.storageDetailsGrid.setData(_.sortBy(this.selectedStorage.organizations, function (i) { return i.name.toLowerCase(); }));
                }, () => {
                    this.isBusyDetails = false;
                });
            }
        }
    }

    indexRowClickedCallback(event): void {
        if (event?.node?.data?.storageUseType === StorageUseType.INDEX_STORE) {
            this.storagesGrid.gridApi.deselectAll(); // this line will trigger an onSelectionChanged event that is not needed so it's handling is skipped by filtering out 'apiSelectAll' 
            this.onSelectionChanged(event);
        }
    }

    addNewStorage(): void {
        this.commonService.openModal(StorageModalComponent,
            { backdrop: false, modalData: { storageOperationType: OperationType.new, indexStorageExists: this.indexStorageExists }, class: 'horizontal-px-560 vertical-px-400' }).then((result) => {
                if (result.modalResult) {
                    this.loadGrid();
                }
            });
    }

    openDeleteModal(storage) {
        this.commonService.openModal(DeleteEntityModalComponent, {
            modalData: new Storage({
                handle: storage.handle,
                name: storage.name,
                isInUse: false
            })
        }).then(response => {
            if (response.modalResult) {
                setTimeout(() => {
                    this.loadGrid();
                    this.storageDetailsGrid.setData([]);
                }, 1000);
            }
        });
    }

    openViewEditModal(storage) {
        const isInUse = storage?.noOfAttachedOrgs > 0 || storage?.noOfAttachedOrgs === -1;    // it's -1 when something went wrong in getting the count for the storage
        this.commonService.openModal(StorageModalComponent, {
            backdrop: false,
            modalData: {
                storageOperationType: OperationType.edit,
                storage: storage,
                isInUse: isInUse
            }, class: 'horizontal-px-560 vertical-px-400'
        }).then(response => {
            if (response.modalResult) {
                setTimeout(() => {
                    this.loadGrid();
                    this.storageDetailsGrid.setData([]);
                });
            }
        });
    }

    saveChanges(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            const storageList = this.storagesGrid.getData();
            let storageFacilityList = [];
            if (storageList?.length > 0) {
                for (let i = 0; i < storageList.length; i++) {
                    if (storageList[i].storageUseType !== StorageUseType.INDEX_STORE) {
                        storageFacilityList.push({ facilityId: parseInt(storageList[i].handle), doNotUse: !storageList[i].inUse, deactivationThreshold: storageList[i].deactivationThreshold, reactivationThreshold: storageList[i].reactivationThreshold, usePriorityOrder: i })
                    }
                }
                if (storageFacilityList.length > 0) {
                    this.restService.post(Endpoint.storageAreaManager_updateStorageFacilityUseCriteria, {
                        systemScope: true,
                        storageUseFacilityCriteriaList: storageFacilityList
                    }).then(() => {
                        this.isDirty = false;
                        this.isBusy = false;
                        this.loadGrid();
                        resolve(true);
                    }, () => {
                        this.isBusy = false;
                        resolve(false);
                    });
                }
            } else {
                resolve(false);
            }
        });
    }

    discardChanges(): void {
        this.loadGrid();
    }

    isDisabled(): boolean {
        this.isDirty = this.storagesHaveThresholdChanges() || this.hasCheckboxChanges || this.hasDragDropChanges;
        return !this.isDirty;
    }

    storagesHaveThresholdChanges(): boolean {
        const listOfJobsWithChangedRate = this.storagesGrid.getData()?.filter(storage => storage.thresholdHasChanged);
        return listOfJobsWithChangedRate?.length > 0;
    }

    onRowDragEnd(event: RowDragEvent): void {
        if (event && (event.type === 'rowDragEnd' || (event.type === 'rowDragLeave' && event.overIndex === 0))) {
            this.hasDragDropChanges = this.storageOrderChanged();
        } else {
            this.hasDragDropChanges = false;
        }
    }

    onRowDragLeave(event: RowDragEvent): void {
        //handles is dirty check in the scenario where dragging a row and dropping it over the Index storage row (is always the first one and not a droppabled area and rowDragEnd is not triggered) 
        this.onRowDragEnd(event);
    }

    storageOrderChanged(): boolean {
        const gridList = this.storagesGrid.getData();
        if (gridList?.length === this.initialStorageHandles.length) {
            for (let i = 0; i < this.initialStorageHandles.length; i++) {
                if (this.initialStorageHandles[i] !== gridList[i]?.handle) {
                    return true;
                }
            }
        }
        return false;
    }

    loadGrid(isFromGetUpdateStatus: boolean = false) {
        if (!isFromGetUpdateStatus) {
            this.isBusy = true;
        }
        this.isDirty = this.hasCheckboxChanges = this.hasDragDropChanges = false;
        const promiseList = [];

        this.initialStorageHandles = [];
        this.restService.post(Endpoint.realmManager_listRemoteNFSStorageAreas, {
            contextHandle: 'super_system_customer'
        }).then(response => {
            this.allStorages = [];
            if (response?.storageAreas?.length > 0) {
                response.storageAreas.forEach(item => {
                    if (item) {
                        item.initialDeactivationThreshold = item.deactivationThreshold;
                        item.initialReactivationThreshold = item.reactivationThreshold;
                        item.thresholdHasChanged = false;
                        item.initialInUse = item.inUse;
                        item.hasEditThresholdPermission = this.hasSystemStorageAddEditPermissions;
                        item.organizations = [];
                        item.noOfAttachedOrgs = 0;
                        this.formatValue(item);

                        if (item.storageUseType === StorageUseType.INDEX_STORE) {
                            promiseList.push(this.restService.post(Endpoint.realmManager_listCustomersForStorageFacility, { handle: item.handle }).then(customersResponse => {
                                item.noOfAttachedOrgs = customersResponse?.organizations?.length ?? -1;
                            }, () => {
                                item.noOfAttachedOrgs = -1;
                            }));
                        } else {
                            promiseList.push(this.restService.post(Endpoint.storageAreaManager_countCustomersForFacility, { handle: item.handle }).then((countResponse: number) => {
                                item.noOfAttachedOrgs = countResponse;
                            }, () => {
                                item.noOfAttachedOrgs = -1;
                            }))
                        }
                    }
                });

                Promise.all(promiseList).then(() => {
                    this.isBusy = false;
                    if (this.allStorages.length > 0) {
                        const idx = this.allStorages.findIndex(s => s.storageUseType === StorageUseType.INDEX_STORE);
                        if (idx > -1) {
                            this.pinnedRowData = [this.allStorages[idx]];
                            this.allStorages.splice(idx, 1);
                        } else {
                            this.pinnedRowData = [];
                            this.indexStorageExists = false;
                        }
                    }

                    const listOrderedByPriority = this.allStorages.length > 0 ? _.sortBy(this.allStorages, function (i) { return i.usePriorityOrder; }) : this.allStorages;
                    this.storagesGrid.setData(listOrderedByPriority);
                    if (listOrderedByPriority?.length > 0) {
                        this.initialStorageHandles = listOrderedByPriority.map(a => a.handle);
                        this.storagesGrid.getRows().forEach(rowNode => {
                            if (rowNode.data.storageUseType === StorageUseType.INDEX_STORE) {
                                rowNode.setRowHeight(30);
                            }
                            else {
                                rowNode.setRowHeight(50);
                            }
                            this.storagesGrid.gridApi.onRowHeightChanged();
                        });
                    }
                    if (isFromGetUpdateStatus && this.selectedStorage) {
                        //restore selected storage if any
                        this.storagesGrid.findNode((node) => node?.data?.handle === this.selectedStorage.handle, true);
                    }
                });
            } else {
                this.isBusy = false;
            }
        }, () => {
            this.isBusy = false
        });
    }

    formatValue(value: any) {
        value.networkPath = value.fqdn ? (value.fqdn + ':' + value.export) : value.export;
        const totalCapacity = parseInt(value.kbAvailable) + parseInt(value.kbUsed);
        value.storageTotal = this.commonService.formatSizeUnits(totalCapacity > 0 ? totalCapacity * 1024 : 0);
        value.storageAvailable = this.commonService.formatSizeUnits(parseInt(value.kbAvailable) > 0 ? parseInt(value.kbAvailable) * 1024 : 0);
        value.currentUsage = totalCapacity > 0 ? (parseInt(value.kbUsed) / totalCapacity * 100).toFixed(0) + ' %' : '';
        if (value.storageUseType === StorageUseType.INDEX_STORE) {
            value.storageUseTypeDisplay = 'Index';
            value.inUse = undefined;
            value.deactivationThreshold = value.reactivationThreshold = undefined;
            this.indexStorageExists = true;
        } else {
            value.storageUseTypeDisplay = 'Document';
            if (value.inUse) {
                if (value.currentUsage === '') {
                    value.readOnlyDisplay = '';
                } else {
                    value.readOnlyDisplay = value.inService ? 'Read/Write' : this.readOnlyModeString;
                }
            }
            else {
                value.readOnlyDisplay = '';
            }
            value.deactivationThreshold = value.deactivationThreshold ?? 80;
            value.reactivationThreshold = value.reactivationThreshold ?? 70;
        }
        this.allStorages.push(value);
    }

    selectedTreeItemChanged(node: GenericTreeNode, projectHandle?: string, settingsType?: SettingsType): void {
        this.hasSystemStorageAddEditPermissions = Permissions.checkUserPermission(ObjectActionListType.STORAGE, ActionType.EDIT);
        if (this.storagesColumnDefs.find(c => c.cellTemplateType === CellTemplateType.contextMenu)) {
            if (!this.hasSystemStorageAddEditPermissions) {
                this.storagesColumnDefs = this.storagesColumnDefs.filter(col => col.cellTemplateType !== CellTemplateType.contextMenu);
                this.hasContextMenuPermissions = false;
            }
        }
        this.loadGrid();
    }

    cellClickedCallback = (rowNode: RowNode, action: CellActionType, event?: CellClickedEvent) => {
        if (rowNode?.data && action === CellActionType.checkbox) {
            rowNode.data.inUse = !rowNode.data.inUse;
            if (event?.api) {
                rowNode.setData(rowNode.data);
            }
            this.checkForCheckboxChangesAndSetDirtyFlag();
        }
    }

    checkForCheckboxChangesAndSetDirtyFlag() {
        this.hasCheckboxChanges = false;
        const allRows = this.storagesGrid.getData();
        if (allRows?.length > 0) {
            for (let i = 0; i < allRows.length; i++) {
                if (allRows[i]?.inUse !== allRows[i]?.initialInUse) {
                    this.hasCheckboxChanges = true;
                    break;
                }
            }
        }
    }

    ngOnDestroy(): void {
        if (this.updateStoragesList) {
            this.updateStoragesList.unsubscribe();
        }
    }
}
